import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { EventTypes } from '@racemap/utilities/consts/events';
import { isNotEmptyString, longestCommonPrefix } from '@racemap/utilities/functions/utils';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import type { Immutable } from 'immer';
import { type FC, useMemo, useRef, useState } from 'react';
import { useOnClickOutside } from 'usehooks-ts';
import { EventTimes } from '../../BasicComponents/EventTimes';
import { Center, HStack } from '../../BasicComponents/MetaComponent';
import { IconArrowDown, IconArrowLeft, IconArrowRight, IconArrowUp } from '../../Icon';

interface Props {
  selectedEventId: string;
  events: Immutable<Array<RacemapEvent>>;

  onEventChange: (eventId: string, slug?: string) => void;
}

export const SelectedEventChooser: FC<Props> = ({ selectedEventId, events, onEventChange }) => {
  const sortedEvents = getSortedEvents(events.concat());
  const [eventChooserVisible, setEventChooserVisible] = useState(false);
  const chooserRef = useRef(null);
  const prefix = useMemo(() => longestCommonPrefix(events.map((e) => e.name)), [events]);

  useOnClickOutside([chooserRef], () => setEventChooserVisible(false));
  const selectedEventIndex = sortedEvents.findIndex((e) => e.id === selectedEventId);
  const selectedEvent = sortedEvents[selectedEventIndex];
  const selectedEventName = getEventName(selectedEvent, prefix);

  const handleLeftClick = () => {
    let newIndex = selectedEventIndex - 1;
    if (newIndex < 0) newIndex = sortedEvents.length - 1;

    const newEvent = sortedEvents[newIndex];
    onEventChange(newEvent.id, newEvent.slug);
  };

  const handleRightClick = () => {
    let newIndex = selectedEventIndex + 1;
    if (newIndex > sortedEvents.length - 1) newIndex = 0;

    const newEvent = sortedEvents[newIndex];
    onEventChange(newEvent.id, newEvent.slug);
  };

  const handleEntryClick = (eventId: string) => {
    const newEvent = sortedEvents.find((e) => e.id === eventId);
    if (newEvent == null) return;

    onEventChange(newEvent.id, newEvent.slug);
    setEventChooserVisible(false);
  };

  return (
    <div>
      <EventSwitcher>
        <ButtonLeft onClick={handleLeftClick}>
          <IconArrowLeft />
        </ButtonLeft>
        {/* prevent to open the chooser again because of a combination of outside click and button click */}
        <Title
          onClick={(e) => {
            e.stopPropagation();
            setEventChooserVisible((value) => !value);
          }}
        >
          {selectedEventName}{' '}
          {eventChooserVisible ? (
            <IconArrowUp className="event-chooser-arrow" />
          ) : (
            <IconArrowDown className="event-chooser-arrow" />
          )}
        </Title>
        <ButtonRight onClick={handleRightClick}>
          <IconArrowRight />
        </ButtonRight>
      </EventSwitcher>
      {eventChooserVisible && (
        <EventChooser ref={chooserRef}>
          {sortedEvents.map((e) => (
            <EventListEntry
              key={e.id}
              onClick={() => handleEntryClick(e.id)}
              selected={e.id === selectedEventId}
            >
              <EventListEntryTitle>{getEventName(e, prefix)}</EventListEntryTitle>
              <EventTimes event={e} compact />
            </EventListEntry>
          ))}
        </EventChooser>
      )}
    </div>
  );
};

const getEventName = (event: Immutable<RacemapEvent>, prefix: string): string => {
  if (event.type === EventTypes.STAGE_GROUP) return 'Overview';
  if (isNotEmptyString(prefix) && event.name.startsWith(prefix))
    return event.name.substring(prefix.length);

  return event.name;
};

const getSortedEvents = (events: Array<Immutable<RacemapEvent>>): Array<Immutable<RacemapEvent>> =>
  events.sort((a, b) =>
    a.type === EventTypes.STAGE_GROUP
      ? -1
      : b.type === EventTypes.STAGE_GROUP
        ? 1
        : a.name.localeCompare(b.name),
  );

const EventChooser = styled.div`
  position: absolute;
  right: 50vw;
  min-width: 400px;
  background: white;
  border: solid 1.5px ${RacemapColors.PaleBlue};
  border-radius: 5px;
  transform: translate(50%, 5px);
  z-index: 100;

  @media screen and (max-width: 576px) {
    width: 100%;
    min-width: unset;
    transform: unset;
    right: 0;
    border: unset;
    border-bottom: solid 1.5px ${RacemapColors.PaleBlue};
    border-radius: unset;
  }
`;

const EventListEntry = styled.div<{ selected: boolean }>`
  font-weight: 400;
  padding: 10px;
  border-bottom: solid 1px ${RacemapColors.LightGray};
  cursor: pointer;
  ${({ selected }) => selected && `background-color: ${RacemapColors.CloudBlue};`}

  :last-of-type {
    border-bottom: unset;
  }

  :hover {
    background-color: ${RacemapColors.LightGray};
  }

  @media screen and (max-width: 576px) {
    font-weight: 400;
    padding: 10px 20px;
  }
`;

const EventListEntryTitle = styled.div`
  font-size: 1.3rem;
  font-weight: 600;
  padding-bottom: 5px;

  @media screen and (max-width: 576px) {
    font-size: 1.1rem;
  }
`;

const EventSwitcher = styled(HStack)`
  height: 45px;
  background-color: ${RacemapColors.PaleBlue}CC;
  color: white;
  font-weight: 400;
  border-bottom: solid 1px ${RacemapColors.LightGray};
  border-top: solid 1px ${RacemapColors.LightGray};

  @media screen and (max-width: 576px) {
    height: 45px;
    font-size: 0.9em;
  }
`;

const Title = styled(Center)`
  cursor: pointer;
  height: 100%;

  .event-chooser-arrow {
    padding-left: 10px;
  }

  @media screen and (max-width: 576px) {
    width: 100%;
  }
`;

const ButtonLeft = styled(Center)`
  width: 50px;
  border-right: solid 1px ${RacemapColors.LightGray};
  height: 100%;
  cursor: pointer;

  :hover {
    background-color: ${RacemapColors.CloudBlue};
    color: ${RacemapColors.DarkGray};
  }
`;

const ButtonRight = styled(Center)`
  width: 50px;
  border-left: solid 1px ${RacemapColors.LightGray};
  height: 100%;
  cursor: pointer;

  :hover {
    background-color: ${RacemapColors.CloudBlue};
    color: ${RacemapColors.DarkGray};
  }
`;
